import { useEffect } from "react";

import type { CallbackParams } from "../lib/auth.ts";

import { useSession } from "./session.tsx";
import { SessionPending, SessionProblem } from "./SessionScreens.tsx";

/**
 * La vuelta del SSO: `?code=…&state=…`.
 *
 * Vive FUERA de `AppShell` a propósito: el armazón arranca el chat, y aquí
 * todavía no hay sesión con la que hablar con nadie.
 */
export function AuthCallback({
  params,
  onDone,
}: {
  readonly params: CallbackParams;
  /** A dónde volver cuando ya hay sesión. */
  readonly onDone: (returnTo: string) => void;
}) {
  const session = useSession();

  useEffect(() => {
    void session.store.complete(params).then((returnTo) => {
      if (returnTo !== null) onDone(returnTo);
    });
    // Una vez por `code`: `completeSignIn` ya es idempotente, y repetirlo por
    // una identidad nueva de `onDone` no aporta nada.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.code]);

  if (session.state.status === "anonymous" && session.state.reason) {
    return (
      <SessionProblem
        title="No se pudo iniciar sesión"
        reason={session.state.reason}
        onSignIn={session.signIn}
        // Con la sesión del SSO viva, "Volver a entrar" trae la misma cuenta
        // de vuelta y puede fallar igual: cerrarla es la salida que no
        // depende de que eso cambie, y deja entrar con otra.
        onSignOut={() => void session.signOut()}
      />
    );
  }

  return <SessionPending>Entrando…</SessionPending>;
}
